import React, {useCallback, useMemo, useRef, useState} from 'react';
import {
  RefreshControl,
  View,
  ScrollView,
  NativeSyntheticEvent,
  NativeScrollEvent,
} from 'react-native';
import theme from '@style';
import globalStore from '@services/global.state';
import HomeMenu from './components/home-menu';
import HomeHeader from './components/home-header';
import Download from './components/download';
import HomeBanner from './components/home-banner';
import HomeSwiperGame from './components/home-swiper-game';
import Spin from '@basicComponents/spin';
import Drawer, {DrawerRef} from '@basicComponents/drawer/drawer';
import {BasicObject} from '@/types';
import {getBannerList, getNoticeCheck} from './home.service';
import {useFocusEffect} from '@react-navigation/native';
import HomeLottery from './components/lottery/lottery';
import HomeHot from './components/hot/hot';
import HomeScratch from './components/home-scratch';
import HomeLive from './components/home-live';
import HomeCasino from './components/home-casino';
import homeStore from './home.refresh.service';
import {BannerListItem} from './home.type';
import RightMenus from '../casino/components/right-menus';
import {getUserVersion, inApp, useResponsiveDimensions} from '@/utils';
import {first} from 'rxjs/operators';
import HomeGoldArea from './components/home-gold-area';
import SattaFaq from '@/pages/home/components/satta-faq';
import bg from '@assets/imgs/home/home-head-bg.png';
import {useChannelParams} from '@/pages/home/home.hook';
import {sequentialStorage} from '@utils/Storage/SequentialStorage';

const Home = () => {
  const isA = useMemo(() => getUserVersion(), []);
  const [pageLoading, setPageLoading] = useState(true);
  const menuRef = React.useRef<DrawerRef>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [bannerList, setBannerList] = useState<BannerListItem[]>();
  const [version, setVersion] = useState(0);
  const {height: screenHeight} = useResponsiveDimensions();
  const scrollRef = useRef<ScrollView>(null);
  const setMenuOpen = (bool: boolean, _unreadCount?: number) => {
    if (bool) {
      setUnreadCount(_unreadCount || 0);
      menuRef.current?.open();
    } else {
      menuRef.current?.close();
    }
  };
  const [refreshing, setRefreshing] = useState(false);
  const [noticeMap, setNoticeMap] = useState<BasicObject>({
    FREE_LOTTERY: 0,
    REBATE: 0,
    LUCKY_SPIN: 0,
  });
  const [showTop, setShowTop] = useState(false);
  React.useEffect(() => {
    getBannerList(2)
      .then(banner => {
        setBannerList(banner);
      })
      .finally(() => setPageLoading(false));
  }, []);
  useChannelParams();
  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const scrollY = event.nativeEvent.contentOffset.y;
    if (scrollY > screenHeight && !showTop) {
      setShowTop(true);
    }
    if (scrollY < screenHeight && showTop) {
      setShowTop(false);
    }
  };

  const handleToTop = () => {
    scrollRef.current?.scrollTo({animated: true, y: 0});
  };

  const doNotice = (token: string | null) => {
    if (token) {
      getNoticeCheck().then(noticeList => {
        const newNoticeMap: BasicObject = {};
        Object.keys(noticeMap).forEach(key => {
          const notice = noticeList.find(item => item.noticeKey === key);
          if (notice) {
            newNoticeMap[key] = notice.noticeCount;
          }
        });
        setNoticeMap(newNoticeMap);
      });
    } else {
      setNoticeMap({
        FREE_LOTTERY: 0,
        REBATE: 0,
        LUCKY_SPIN: 0,
      });
    }
  };
  const [tabIndex, setTabIndex] = useState<0 | 1 | 2 | 3 | 4 | 5>(0);
  const refreshCount = React.useRef(0);
  const handleFocusEffect = React.useCallback(() => {
    const sub = globalStore.tokenSubject.subscribe(token => {
      doNotice(token);
      globalStore.amountCheckOut.next({});
      sequentialStorage.onHomeFocused();
      if (token) {
        globalStore.refreshNotification.next();
      }
    });
    homeStore.refreshEnd.pipe(first()).subscribe(() => {
      setPageLoading(false);
    });
    const refreshedSub = homeStore.refreshEnd.subscribe(() => {
      if (++refreshCount.current === 2) {
        refreshCount.current = 0;
        setRefreshing(false);
      }
    });
    const changeTabSub = homeStore.changeTab.subscribe(index => {
      setTabIndex(index);
    });

    return () => {
      sub.unsubscribe();
      refreshedSub.unsubscribe();
      changeTabSub.unsubscribe();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useFocusEffect(handleFocusEffect);

  const homeRefresh = () => {
    doNotice(globalStore.token);
    if (globalStore.token) {
      sequentialStorage.loadData();
    }
    homeStore.refresh.next();
  };
  useFocusEffect(
    useCallback(() => {
      sequentialStorage.emit('updated');
    }, []),
  );

  return (
    <Spin loading={pageLoading} style={[theme.flex.col, theme.fill.fill]}>
      <div className="relative flex flex-col h-full w-full">
        <img
          src={bg}
          className="absolute left-0 top-0 h-[13.125rem] w-full bg-cover"
          alt=""
        />
        <HomeHeader setMenuOpen={setMenuOpen} version={version} />
        {inApp ? null : <Download />}
        <ScrollView
          ref={scrollRef}
          style={[theme.flex.flex1]}
          // stickyHeaderIndices={[2]}
          scrollEventThrottle={16}
          onScroll={handleScroll}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => {
                setRefreshing(true);
                homeRefresh();
                setVersion(v => v + 1);
              }}
            />
          }>
          <HomeBanner bannerList={bannerList} />
          {isA ? (
            <>
              <HomeSwiperGame />
              <HomeGoldArea />
            </>
          ) : (
            <HomeGoldArea />
          )}
          {/*<HomeTab*/}
          {/*  value={tabIndex}*/}
          {/*  setValue={value => {*/}
          {/*    showMap[value] = true;*/}
          {/*    setTabIndex(value);*/}
          {/*  }}*/}
          {/*  isA={isA}*/}
          {/*/>*/}
          {(isA
            ? [HomeHot, HomeLive, HomeCasino, null, HomeScratch, HomeLottery]
            : [HomeHot, HomeScratch, HomeLive, HomeCasino]
          ).map((V, i) => (
            <View
              key={i}
              style={[
                // eslint-disable-next-line react-native/no-inline-styles
                {height: i === tabIndex ? 'auto' : 0, overflow: 'hidden'},
              ]}>
              {V ? <V /> : null}
              {/*{showMap[i] ? <V /> : null}*/}
            </View>
          ))}
          <SattaFaq />
        </ScrollView>
        <RightMenus hasTop={showTop} hasBonus onTop={handleToTop} />
      </div>
      <Drawer drawerWidth={280} ref={menuRef}>
        <HomeMenu setMenuOpen={setMenuOpen} unreadCount={unreadCount} />
      </Drawer>
    </Spin>
  );
};

export default Home;
